import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { useLang, BRANDS, SUSPENSIONS } from "@/lib/i18n";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/shop/ProductCard";
import { MissingSeatForm } from "@/components/shop/MissingSeatForm";

const FilterGroup = ({ title, options, value, onChange, label, name }) => {
  const { t } = useLang();
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-x text-slate-500 mb-3">{title}</div>
      <div className="flex flex-wrap lg:flex-col gap-2">
        {["", ...options].map((o) => (
          <button key={o || "all"} data-testid={`filter-${name}-${(o || "all").toLowerCase().replace(" ", "-")}`}
            onClick={() => onChange(o)}
            className={`text-left px-3 h-9 border text-sm transition-colors ${value === o ? "border-orange-500 text-orange-500 bg-orange-500/5" : "border-[#3B4157] text-slate-300 hover:border-slate-500"}`}>
            {o ? label(o) : t("all")}
          </button>
        ))}
      </div>
    </div>
  );
};

export default function Catalog() {
  const { t } = useLang();
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const results = useRef(null);
  const toResults = () => window.innerWidth < 1024 && setTimeout(() => results.current?.scrollIntoView({ behavior: "smooth" }), 50);
  const f = {
    brand: sp.get("brand") || "", machine_type: sp.get("machine_type") || "",
    suspension: sp.get("suspension") || "", q: sp.get("q") || "",
    in_stock: sp.get("in_stock") === "1", sort: sp.get("sort") || "new", fits: sp.get("fits") || "",
  };
  const [fitsList, setFitsList] = useState([]);
  useSeo({ title: [f.brand, sp.get("fits"), t("seo_catalog_title")].filter(Boolean).join(" – "), description: t("seo_catalog_desc") });
  useEffect(() => { if (sp.get("fits")) toResults(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/products/fits", { params: f.brand ? { brand: f.brand } : {} }).then((r) => setFitsList(r.data));
  }, [f.brand]);
  const key = sp.toString();

  useEffect(() => {
    const params = Object.fromEntries([...new URLSearchParams(key)].filter(([, v]) => v));
    if (params.in_stock) params.in_stock = true;
    api.get("/products", { params }).then((r) => setProducts(r.data));
  }, [key]);

  const searched = useRef("");
  useEffect(() => {
    const q = (f.q || "").trim();
    if (q.length < 2 || searched.current === q.toLowerCase()) return;
    const tm = setTimeout(() => {
      searched.current = q.toLowerCase();
      api.post("/track/search", { q, results: products.length }).catch(() => {});
    }, 1500);
    return () => clearTimeout(tm);
  }, [f.q, products]);

  const set = (k, v) => {
    const n = new URLSearchParams(sp);
    v ? n.set(k, v) : n.delete(k);
    setSp(n);
  };

  return (
    <div data-testid="catalog-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
      <div className="text-xs font-mono-x uppercase tracking-[0.25em] text-orange-500">// {t("nav_catalog")}</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
        {f.brand || t("view_all")}
      </h1>
      <div className="grid lg:grid-cols-[220px_1fr] gap-10 mt-10">
        <aside className="space-y-8" data-testid="catalog-filters">
          <input data-testid="catalog-search-input" defaultValue={f.q} key={f.q} placeholder={t("search")}
            onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)}
            className="w-full h-10 px-3 bg-[#292D3C] border border-[#3B4157] text-sm outline-none focus:border-orange-500" />
          <FilterGroup name="brand" title={t("brand")} options={BRANDS} value={f.brand}
            onChange={(v) => { const n = new URLSearchParams(sp); v ? n.set("brand", v) : n.delete("brand"); n.delete("fits"); setSp(n); }} label={(o) => o} />
          {fitsList.length > 0 && <FilterGroup name="fits" title={t("by_brand")} options={fitsList} value={f.fits} onChange={(v) => { set("fits", v); toResults(); }} label={(o) => o} />}
          <FilterGroup name="suspension" title={t("suspension")} options={SUSPENSIONS} value={f.suspension} onChange={(v) => set("suspension", v)} label={t} />
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
            <input data-testid="filter-in-stock" type="checkbox" checked={f.in_stock} onChange={(e) => set("in_stock", e.target.checked ? "1" : "")} className="accent-orange-500 w-4 h-4" />
            {t("only_stock")}
          </label>
        </aside>
        <div ref={results} className="scroll-mt-20">
          <div className="flex items-center justify-between border-b border-[#3B4157] pb-4 mb-6">
            <span data-testid="catalog-results-count" className="text-sm font-mono-x text-slate-400">{t("results")}: {products.length}</span>
            <select data-testid="catalog-sort-select" value={f.sort} onChange={(e) => set("sort", e.target.value)}
              className="h-9 bg-[#292D3C] border border-[#3B4157] text-sm px-2 outline-none focus:border-orange-500">
              <option value="new">{t("sort_new")}</option>
              <option value="popular">{t("sort_pop")}</option>
              <option value="price_asc">{t("sort_pa")}</option>
              <option value="price_desc">{t("sort_pd")}</option>
            </select>
          </div>
          {f.fits && <p data-testid="catalog-fits-note" className="text-sm text-slate-400 -mt-2 mb-6">{t("fits_note")} <span className="text-orange-400">{f.fits}</span></p>}
          {products.length === 0 && <p data-testid="catalog-no-results" className="text-slate-400 py-10 text-center">{t("no_results")}</p>}
          {products.length === 0 && f.q && <MissingSeatForm key={f.q} query={f.q} />}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
